/**
 * HeaderPopover Container Component
 *
 * Renders the settings/menu popover in the header. Delegates settings data
 * and actions to the shared useSettingsActions hook so the same logic can
 * be reused by SidePanel on web.
 * @module components/HeaderPopover/HeaderPopoverContainer
 */

import { TEST_ID_MENU_HEADER_BUTTON } from "e2e/testId";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisIcon,
  SettingsIcon,
} from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";

import HeaderPopoverView from "./HeaderPopoverView";

import RowButton from "@/components/molecules/RowButton";
// design-system: button size snapped xl→lg (closed size scale)
import { Button } from "@/components/atoms/Button";

import useMobileScreen from "@/hooks/useMobileScreen";
import useSettingsActions from "@/hooks/useSettingsActions";
import { useTheme } from "@/providers/ThemeProvider";

/** Props for HeaderPopoverContainer */
interface HeaderPopoverContainerProps {
  /** Optional custom trigger renderer. When provided, overrides the default ellipsis button. */
  readonly renderTrigger?: (triggerProps: object) => React.JSX.Element;
}

/**
 * Container component for header popover menu.
 * Manages popover open/close state and manage menu toggle while
 * delegating all settings logic to the shared useSettingsActions hook.
 * @param root0 - Component props
 * @param root0.renderTrigger - Optional custom trigger renderer, overrides default ellipsis button
 */
const HeaderPopoverContainer = ({
  renderTrigger: externalTrigger,
}: HeaderPopoverContainerProps = {}) => {
  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);
  const [isManageMenuOpen, setIsManageMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();

  const handleOpenFilters = useCallback(() => setIsOpenFilters(true), []);
  const handleCloseFilters = useCallback(() => {
    setIsOpenFilters(false);
    setIsManageMenuOpen(false);
    setIsLanguageMenuOpen(false);
  }, []);

  const settings = useSettingsActions(handleCloseFilters);

  const handleManageMenuToggle = useCallback(
    () => setIsManageMenuOpen(prev => !prev),
    []
  );

  const handleLanguageMenuToggle = useCallback(
    () => setIsLanguageMenuOpen(prev => !prev),
    []
  );

  const languageHandlers = useMemo(
    () =>
      Object.fromEntries(
        settings.languageOptions.map(option => [
          option.code,
          () => settings.onLanguageSelect(option.code),
        ])
      ) as Record<string, () => void>,
    [settings]
  );

  const renderPopoverTrigger = useCallback(
    (triggerProps: object) => (
      <Button
        {...triggerProps}
        size="lg"
        variant="link"
        testID={TEST_ID_MENU_HEADER_BUTTON}
        className="rounded-none px-3 py-2"
        accessible
        accessibilityLabel="Open menu"
        accessibilityRole="button"
        icon={EllipsisIcon}
      />
    ),
    []
  );

  const isDark = resolvedTheme === "dark";

  const renderManageMenuTrigger = useCallback(
    () => (
      <RowButton
        label="Manage"
        leftIcon={isManageMenuOpen ? ChevronDownIcon : ChevronRightIcon}
        rightIcon={SettingsIcon}
        isDark={isDark}
        onPress={handleManageMenuToggle}
      />
    ),
    [isManageMenuOpen, handleManageMenuToggle, isDark]
  );

  return (
    <HeaderPopoverView
      organizationName={settings.organizationName}
      isOpen={isOpenFilters}
      onOpen={handleOpenFilters}
      onClose={handleCloseFilters}
      onDeactivateMe={settings.onDeactivateMe}
      onSignOut={settings.onSignOut}
      onSignOutAllDevices={settings.onSignOutAllDevices}
      isAdmin={settings.isAdmin}
      isSmallScreen={isSmallScreen}
      numberOfValidOrganizations={settings.numberOfValidOrganizations}
      hasPendingInvitations={settings.hasPendingInvitations}
      isManageMenuOpen={isManageMenuOpen}
      isDark={isDark}
      mode={settings.mode}
      themeIcon={settings.themeIcon}
      themeLabel={settings.themeLabel}
      onThemeToggle={settings.onThemeToggle}
      languageLabel={settings.languageLabel}
      languageIcon={settings.languageIcon}
      languageOptions={settings.languageOptions}
      languageHandlers={languageHandlers}
      selectedLanguageCode={settings.selectedLanguageCode}
      isLanguageMenuOpen={isLanguageMenuOpen}
      onLanguageMenuToggle={handleLanguageMenuToggle}
      versionString={settings.versionString}
      onVersionPress={settings.onVersionPress}
      renderPopoverTrigger={externalTrigger ?? renderPopoverTrigger}
      renderManageMenuTrigger={renderManageMenuTrigger}
      manageItemHandlers={settings.manageItemHandlers}
    />
  );
};

export default HeaderPopoverContainer;
